import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import {
  INumberEntryProps,
  NumberCard,
} from "@/components/pageCards/numberCard";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { Terminal } from "server/dwhApi/terminals/types.js";

type Heading = {
  id: string;
  heading: string;
};

const getRealEstateData = (
  headings: Heading[],
  data?: Terminal,
): INumberEntryProps[] => {
  if (!data) {
    return headings.map((h) => ({ heading: h.heading, data: "-", suffix: "" }));
  }
  const { totalWasteWeight, totalResidualWasteWeight, sortingDegree } = data;
  const values: { [k: string]: string | number } = {
    residual: totalResidualWasteWeight.quantity,
    recycled: totalWasteWeight.quantity - totalResidualWasteWeight.quantity,
    sortingRate: sortingDegree * 100,
  };

  const numbers: INumberEntryProps[] = headings.map((h) => ({
    ...h,
    data: values[h.id],
    suffix: h.id === "sortingRate" ? " %" : "",
  }));

  return numbers;
};

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const OverviewKeyNumbersRE: React.FC<Props> = ({
  timeFilter: persistedTimeFilter,
  setTimeFilters,
}) => {
  const { t } = useTrans();
  const { setKeyNumbersLoading } = useHomeLoadingState();
  const { terminalConfig } = useConfigService();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);

  const defaultPeriod = useMemo(
    () =>
      persistedTimeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, persistedTimeFilter],
  );

  const filters: FilterSort[] = useMemo(
    () => [createTimeFilters({ defaultValue: defaultPeriod })],
    [defaultPeriod],
  );

  const { currentTerminal } = useTerminalsState();

  const timeFilter = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const { data, isLoading } = trpc.dwh.terminals.getById.useQuery(
    {
      ...timeFilter,
      terminalId: currentTerminal?.id || "",
    },
    {
      queryKeyHashFn: () =>
        [
          "terminal",
          currentTerminal?.id,
          timeFilter.startTimeISO,
          timeFilter.endTimeISO,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  useEffect(() => {
    setKeyNumbersLoading(isLoading);
  }, [setKeyNumbersLoading, isLoading]);

  const headings: Heading[] = useMemo(
    () => [
      {
        id: "residual",
        heading: `${t("circularityLabels:residualWaste")} (kg)`,
      },
      {
        id: "recycled",
        heading: `${t("circularityLabels:recycled")} (kg)`,
      },
      { id: "sortingRate", heading: t("circularityLabels:recyclingRate") },
    ],
    [t],
  );

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.KeyNumbers,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  const view = useMemo(() => {
    const numbers = getRealEstateData(headings, data?.terminal);

    return (
      <NumberCard
        title="circularityLabels:keyNumbers"
        numbers={numbers}
        filters={filters}
        isLoading={isLoading}
        onFilterOptionChange={onFilterOptionChange}
        filterLocation={CardComponentFilterLocation.RIGHT}
      />
    );
  }, [isLoading, data, filters, headings, onFilterOptionChange]);

  return view;
};
